import { Button } from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import { useTranslation } from 'next-i18next';
import { FC, RefObject, useCallback } from 'react';

import { DrawByProductId } from '../../features/draws/types';
import { useDrawStage } from '../../hooks/useDrawStage';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ProductStatus } from '../../types/Analytics';
import { ReleaseType } from '../../types/Products';
import { PurchasingState } from '../../types/Purchasing';
import { ButtonLocation } from '../../types/Subscriptions';
import { NFTProductType } from '../../types/web3/product';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { SignUpButton } from '../Containers/PurchaseForm/SignUpButton';
import { DrawStickyButtons } from './DrawStickyButtons';
import { getDrawSubscribeState } from './getDrawSubscribeState';

interface Props {
  anchorRef: RefObject<HTMLAnchorElement>;
  buttonLocation?: ButtonLocation;
  /** Rendered in the bottom sticky bar: 44px buttons, and a waitlist button when sold out. */
  bottomBar?: boolean;
  draw?: DrawByProductId;
  /** Fill the row, rather than sitting beside the bar's urgency text. */
  fullWidth?: boolean;
  product: ProductType | NFTProductType;
  productRef: string;
  productState: PurchasingState;
  productStatus: ProductStatus;
  releaseType: ReleaseType;
  unlock: boolean;
}

export const ProductStickyButtons: FC<Props> = ({
  anchorRef,
  bottomBar = false,
  buttonLocation = 'product page sticky',
  draw,
  fullWidth = false,
  product,
  productRef,
  productState,
  productStatus,
  releaseType,
  unlock,
}) => {
  const { t } = useTranslation('product');
  const isMobile = useMediaQuery('sm');
  const drawStage = useDrawStage(draw);
  // 44px in the bottom bar, per the design; the transactional block's own CTA is 52px.
  const buttonSize = bottomBar ? 'md' : isMobile ? 'sm' : 'md';
  const [emitter] = useAnalyticsEmitter({
    'button_location': buttonLocation,
    'product_id': !('tokenId' in product) ? product.id[0]?.id : '',
    'product_status': productStatus,
    'product_title': product.title,
  });

  const handleClick = useCallback(
    (buttonState: string) => {
      if (anchorRef.current) {
        anchorRef.current.scrollIntoView({
          behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
        });
      }
      emitter('sticky button', 'clicked', {
        'button_state': buttonState,
      });
    },
    [anchorRef, emitter],
  );

  const isDraw = (releaseType === 'draw' || releaseType === 'auction') && draw;

  const drawSubscribeState = getDrawSubscribeState({
    bottomBar,
    draw,
    drawStage,
    releaseType,
  });

  if (drawSubscribeState === 'UPCOMING') {
    return (
      <SignUpButton
        buttonLocation={buttonLocation}
        fullWidth={fullWidth}
        labels={{ normal: t('notifyMe'), success: t('subscribed') }}
        onClick={() => handleClick('notify me')}
        onClickSkipSubscription
        product={product}
        productRef={productRef}
        productStatus={productStatus}
        showIcon
        size={buttonSize}
      />
    );
  }

  if (drawSubscribeState === 'SOLD_OUT') {
    return (
      <SignUpButton
        buttonLocation={buttonLocation}
        fullWidth={fullWidth}
        isSoldOut
        labels={{
          normal: t('subscriptionCta.soldOut.buttonTitle'),
          success: t('subscribed'),
        }}
        onClick={() => handleClick('join waitlist')}
        onClickSkipSubscription
        product={product}
        productRef={productRef}
        productStatus={productStatus}
        size={buttonSize}
      />
    );
  }

  if (isDraw) {
    return (
      <DrawStickyButtons
        bottomBar={bottomBar}
        draw={draw}
        fullWidth={fullWidth}
        isAuction={releaseType === 'auction'}
        isMobile={isMobile}
        onClick={(isPreOrder: boolean) => {
          handleClick(isPreOrder ? 'pre-order' : releaseType);
        }}
      />
    );
  }

  switch (productState) {
    case 'COMING_SOON':
      return (
        <SignUpButton
          buttonLocation={buttonLocation}
          fullWidth={fullWidth}
          labels={{ normal: t('notifyMe'), success: t('subscribed') }}
          onClick={() => handleClick('notify me')}
          onClickSkipSubscription
          product={product}
          productRef={productRef}
          productStatus={productStatus}
          showIcon
          size={buttonSize}
        />
      );
    case 'ENQUIRY':
      return (
        <Button
          fullWidth={fullWidth}
          label={t('enquire')}
          onClick={() => handleClick('enquire')}
          size={buttonSize}
          variant="primary"
        />
      );
    case 'AVAILABLE':
      return (
        <Button
          fullWidth={fullWidth}
          label={unlock ? t('unlock') : t('goToCheckout')}
          onClick={() => handleClick(unlock ? 'unlock' : 'buy now')}
          size={buttonSize}
          variant="primary"
        />
      );
    case 'SOLD_OUT':
      if (!bottomBar) return null;

      return (
        <SignUpButton
          buttonLocation={buttonLocation}
          fullWidth={fullWidth}
          isSoldOut
          labels={{
            normal: t('subscriptionCta.soldOut.buttonTitle'),
            success: t('subscribed'),
          }}
          onClick={() => handleClick('join waitlist')}
          onClickSkipSubscription
          product={product}
          productRef={productRef}
          productStatus={productStatus}
          size={buttonSize}
        />
      );
    default:
      return null;
  }
};
